/**
 * @file FullScreenLoader.tsx
 * @brief Branded full-viewport boot loader used during auth bootstrap and
 *        long-running redirects.
 *
 * @details
 * Animates the Verblizr mark, then holds it. `LOADER_MARK` picks the
 * animation: 'flipBoard' (departure board through six languages, same as the
 * phone splash; DES 03-10-2026) or 'draw' (outline strokes, then fills; the
 * original, kept to switch back). Shared by all auth/root call sites.
 *
 * Words appear only when the wait is real (DES 03-10-2026, replacing an
 * always-on "Verifying session…" that flashed for under a second):
 * - 0–3s: the mark alone, like the phone splash
 * - from 3s: the caller's `label` ("Signing you in…", "Getting things ready…")
 * - from 8s: a plain hint that the connection may be slow
 * - `immediate` (21-10-2026): the label from the start and for good, e.g.
 *   "Reconnecting…" while the server cannot be reached (the session is kept)
 * The line's height is reserved from the start so the mark never jumps, and
 * screen readers always get a "Loading" status.
 *
 * Desktop app: the first loader after launch holds a still mark, because the
 * launch splash played the Flip board over it a moment ago. Not after a
 * reload: no splash plays then, so the Flip board does (03-10-2026).
 *
 * @author DES
 * @date 26-05-2026
 */

import { useEffect, useState } from "react";
import { BrandLogoDraw } from "@/components/brand/BrandLogoDraw";
import { BrandFlipBoard } from "@/components/brand/BrandFlipBoard";
import { cn } from "@/shared/theme/cn";
import { desktopShell } from "@/shared/desktop/desktopShell";

/** ⭐ Loader animation in use. Keep in step with the phone's `SPLASH_ANIMATION`. */
const LOADER_MARK: "draw" | "flipBoard" = "flipBoard";

const LABEL_AFTER_MS = 3000;
const SLOW_AFTER_MS = 8000;
/** Two lines on purpose: the sentence break, top-heavy (first line longer). */
const SLOW_TEXT = "This is taking longer than usual.\nCheck your connection.";

type Stage = "quiet" | "label" | "slow";

/** Set once the first loader of this page load has mounted. */
let firstLoaderMounted = false;

/**
 * In the desktop app the launch splash (DESKTOP/src/launchSplash.js) has just
 * played the Flip board over this page, so the first loader of the page load
 * holds the finished mark instead of playing it a second time.
 */
function followsDesktopSplash(): boolean {
  const first = !firstLoaderMounted;
  firstLoaderMounted = true;
  // A reload (⌘R, View › Reload) restarts `performance.now()` at 0 but plays
  // no splash, so it used to hold the still mark too (DES 03-10-2026: "login
  // pe refresh karnay per yeh static image kiyoon ata hai"). A reload animates.
  if (isReload()) return false;
  return first && desktopShell() !== null && performance.now() < 15000;
}

function isReload(): boolean {
  try {
    const nav = performance.getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;
    return nav?.type === "reload";
  } catch {
    return false;
  }
}

export function FullScreenLoader({ label, immediate = false }: { label?: string; immediate?: boolean }) {
  const [stage, setStage] = useState<Stage>("quiet");
  const [still] = useState(followsDesktopSplash);

  useEffect(() => {
    const showLabel = window.setTimeout(() => setStage("label"), LABEL_AFTER_MS);
    const showSlow = window.setTimeout(() => setStage("slow"), SLOW_AFTER_MS);
    return () => {
      window.clearTimeout(showLabel);
      window.clearTimeout(showSlow);
    };
  }, []);

  const text = immediate
    ? label
    : stage === "slow"
      ? SLOW_TEXT
      : stage === "label"
        ? label
        : undefined;

  // overflow-hidden: the Flip board's zoom exit grows past the viewport.
  return (
    <div className="flex min-h-screen items-center justify-center overflow-hidden bg-background px-4">
      <div className="flex flex-col items-center gap-4 text-muted-foreground">
        {LOADER_MARK === "flipBoard" ? <BrandFlipBoard size={80} still={still} /> : <BrandLogoDraw size={80} />}
        <p
          className={cn(
            "min-h-5 max-w-xs whitespace-pre-line text-center text-sm transition-opacity duration-300",
            text ? "opacity-100" : "opacity-0",
          )}
          aria-hidden
        >
          {text}
        </p>
        <span className="sr-only" role="status" aria-live="polite">
          {immediate ? label : stage === "slow" ? SLOW_TEXT : "Loading"}
        </span>
      </div>
    </div>
  );
}
